import React, { useEffect, useState } from 'react';
import { SiteSettings } from '../../types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { fetchSetting, updateSetting } from '../../features/admin/settingSlice';
import InnerLoading from '../ui/InnerLoading';
import { FiEdit3, FiSave, FiX } from 'react-icons/fi';

const SettingPage = () => {
    const { t,i18n} = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const { siteInfo, loading } = useSelector((state: RootState) => state.settingSlice);

    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<SiteSettings | null>(null);
    const [logoPreview, setLogoPreview] = useState('');
    const [whiteLogoPreview, setWhiteLogoPreview] = useState('');
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [whiteLogoFile, setWhiteLogoFile] = useState<File | null>(null);

    useEffect(() => {
        dispatch(fetchSetting());
    }, [dispatch]);

    useEffect(() => {
        if (siteInfo) {
            setFormData(siteInfo);
            setLogoPreview(siteInfo.darkLogo);
            setWhiteLogoPreview(siteInfo.whiteLogo);
        }
    }, [siteInfo]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!formData) return;
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'dark' | 'white') => {
        const file = e.target.files?.[0];
        if (!file || !formData) return;
        if (type === 'dark') {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        } else {
            setWhiteLogoFile(file);
            setWhiteLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async () => {
        if (!formData) return;
        const data = new FormData();
        for (const key in formData) {
            data.append(key, (formData as any)[key]);
        }
        if (logoFile) data.append('darkLogo', logoFile);
        if (whiteLogoFile) data.append('whiteLogo', whiteLogoFile);
        await dispatch(updateSetting({ data }));
        await dispatch(fetchSetting());
        setIsEditing(false);
    };

    if (loading) return <InnerLoading />;
    if (!formData) return <div>{t('no-data')}</div>;

    return (
        <div>
            <div className='bg-gradient-to-r from-blue-600 to-indigo-500 text-white flex items-center justify-between rounded-xl shadow-md p-6 mb-6'>
                <h2 className="text-white text-lg font-semibold ">{t('Setting-title')}</h2>
                {!isEditing && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className=" bg-primary text-white px-4 py-2 rounded-xl hover:bg-darkColor flex items-center gap-2"
                    >
                        <FiEdit3 />
                        {t('edit')}
                    </button>
                )}
            </div>

            <div className="grid bg-white w-full p-2 rounded-lg grid-cols-1 md:grid-cols-2 mb-5 gap-6">
                {Object.entries(formData).map(([key, value]) => {
                    const excludedKeys = ['id', 'createAt', 'darkLogo', 'whiteLogo'];
                    if (excludedKeys.includes(key)) return null;
                    return (
                        <div key={key} className="relative">
                            <input
                                type="text"
                                id={key}
                                name={key}
                                disabled={!isEditing}
                                value={value}
                                onChange={handleChange}
                                className={`peer w-full border border-grayColor rounded-xl px-4 pt-5 pb-2 focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100`}
                                placeholder=" "
                            />
                            <label
                                htmlFor={key}
                                className={`absolute ${i18n.language==="ar"?"right-3":"left-3"} top-2 text-gray-500 text-sm transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-2 peer-focus:text-sm peer-focus:text-primary`}
                            >
                                {t(`${key}`)}
                            </label>
                        </div>
                    );
                })}
            </div>
            <div className="grid w-full md:grid-cols-2 gap-8">
                {/* Dark Logo */}
                <div className="space-y-4">
                    <label className="text-gray-700 text-sm font-semibold">{t('darkLogo')}</label>

                    {isEditing ? (
                        <div className="group relative flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-2xl bg-gray-50 px-6 py-8 cursor-pointer hover:border-primary transition">
                            <input
                                type="file"
                                accept="image/*"
                                className="absolute inset-0 opacity-0 cursor-pointer z-10"
                                onChange={(e) => handleLogoChange(e, 'dark')}
                            />
                            <div className="flex flex-col items-center text-gray-500 z-0">
                                <svg className="w-10 h-10 mb-2 text-primary" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M12 12v9m0 0l3-3m-3 3l-3-3" />
                                </svg>
                                <p className="text-sm">{t("upload-instruction") || "Drag & drop or click to upload"}</p>
                            </div>
                            {logoPreview && (
                                <img
                                    src={logoPreview}
                                    alt="dark logo preview"
                                    className="absolute top-2 right-2 w-12 h-12 object-contain rounded border bg-white p-1 shadow-md"
                                />
                            )}
                        </div>
                    ) : (
                        <div className="w-full border rounded-2xl bg-gray-50 p-4 flex justify-center items-center">
                            <img
                                src={logoPreview}
                                alt="dark logo"
                                className="w-32 h-32 object-contain rounded"
                            />
                        </div>
                    )}
                </div>

                {/* White Logo */}
                <div className="space-y-4">
                    <label className="text-gray-700 text-sm font-semibold">{t('whiteLogo')}</label>

                    {isEditing ? (
                        <div className="group relative flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-2xl bg-gray-50 px-6 py-8 cursor-pointer hover:border-primary transition">
                            <input
                                type="file"
                                accept="image/*"
                                className="absolute inset-0 opacity-0 cursor-pointer z-10"
                                onChange={(e) => handleLogoChange(e, 'white')}
                            />
                            <div className="flex flex-col items-center text-gray-500 z-0">
                                <svg className="w-10 h-10 mb-2 text-primary" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M12 12v9m0 0l3-3m-3 3l-3-3" />
                                </svg>
                                <p className="text-sm">{t("upload-instruction") || "Drag & drop or click to upload"}</p>
                            </div>
                            {whiteLogoPreview && (
                                <img
                                    src={whiteLogoPreview}
                                    alt="white logo preview"
                                    className="absolute top-2 right-2 w-12 h-12 object-contain rounded border bg-white p-1 shadow-md"
                                />
                            )}
                        </div>
                    ) : (
                        <div className="w-full border rounded-2xl bg-gray-50 p-4 flex justify-center items-center">
                            <img
                                src={whiteLogoPreview}
                                alt="white logo"
                                className="w-32 h-32 object-contain rounded"
                            />
                        </div>
                    )}
                </div>
            </div>

            {isEditing && (
                <div className="flex justify-end gap-4 pt-6 border-t">
                    <button
                        onClick={handleSubmit}
                        className="bg-primary text-white px-6 py-2 rounded-xl hover:bg-green-700 flex items-center gap-2"
                    >
                        <FiSave />
                        {t('save')}
                    </button>
                    <button
                        onClick={() => {
                            setFormData(siteInfo);
                            setLogoFile(null);
                            setWhiteLogoFile(null);
                            setLogoPreview(siteInfo?.darkLogo || '');
                            setWhiteLogoPreview(siteInfo?.whiteLogo || '');
                            setIsEditing(false);
                        }}
                        className="bg-gray-300 px-6 py-2 rounded-xl hover:bg-gray-400 flex items-center gap-2"
                    >
                        <FiX />
                        {t('cancel')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default SettingPage;
